import React from "react";
import { Box, makeStyles, Typography } from "@material-ui/core";

import { Camera, EventCircle, type EventType, ExternalLink } from "@eroad/icons/svg";

import useEventColour from "../../hooks/useEventColour";
import type { CoretexTheme } from "../../theme";
import { EventColour } from "./types";

const useStyles = makeStyles((theme: CoretexTheme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    width: "260px",
    height: "40px",
    padding: theme.spacing(2),

    "& > *:not(:last-child)": {
      marginRight: theme.spacing(2),
    },
    backgroundColor: theme.palette.mono.white,
    borderRadius: theme.shape.borderRadius,
    cursor: "pointer",
    boxShadow: theme.elevation.standard,
  },
  icon: {
    height: "24px",
    width: "24px",
  },
  title: {
    fontSize: theme.typography.body2.fontSize,
    color: theme.palette.mono.primaryDark,
    flex: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  incidentIcon: {
    fill: theme.palette.mono.secondaryDark,
    width: 16,
  },
  time: {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.mono.secondaryDark,
    minWidth: 43,
    maxWidth: 70,
  },
}));

export interface EventOverlayProps {
  title: string;
  eventType: EventType;
  eventColour?: EventColour;
  eventTime: string;
  isIncidentEvent?: boolean;
  isCameraEvent?: boolean;
  overlayOnClick?: () => void;
}

const EventOverlay = ({
  title,
  eventColour = EventColour.General,
  eventType,
  isIncidentEvent,
  isCameraEvent,
  eventTime,
  overlayOnClick,
}: EventOverlayProps): JSX.Element => {
  const classes = useStyles();
  const eventColourFill = useEventColour(eventColour);

  const IncidentIcon = (): React.ReactElement => {
    if (isIncidentEvent && !isCameraEvent) {
      return <ExternalLink data-testid="external-link" className={classes.incidentIcon} />;
    }

    if (!isIncidentEvent && isCameraEvent) {
      return <Camera data-testid="camera-icon" className={classes.incidentIcon} />;
    }

    return <></>;
  };

  return (
    <Box data-testid="event-overlay" className={classes.root} onClick={overlayOnClick}>
      <EventCircle className={classes.icon} eventType={eventType} fill={eventColourFill} />
      <Typography className={classes.title}>{title}</Typography>
      <IncidentIcon />
      <Typography className={classes.time}>{eventTime}</Typography>
    </Box>
  );
};

export default EventOverlay;
